/**
 * Alert engine — Stage 3 deliverable.
 *
 * Lightweight, browser-side rule evaluator that watches the global
 * `/ws/orders/` stream and fires desktop notifications + a soft chime
 * when one of the user's rules trips. The engine is intentionally
 * pessimistic: rules trip at most once per window and recover when the
 * underlying condition clears, so a stuck order doesn't spam notifications.
 *
 * The engine keeps no server state — rule definitions live in
 * `localStorage` (managed by `useAlertRules` below) so users get a
 * personal alert profile on the device they're working on.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocket } from '@/api/ws';
import { storage } from '@/lib/storage';
import { notify } from '@/lib/toast';
import { recordAlertEvent } from '@/api/bi/hooks';

export type AlertRuleKind =
  | 'order_age'
  | 'cancellation_spike'
  | 'store_offline';

export interface AlertRule {
  id: string;
  kind: AlertRuleKind;
  /** Human-friendly name (shown in toast / notification). */
  label: string;
  /** Threshold in seconds (`order_age`, `store_offline`) or per minute (`cancellation_spike`). */
  threshold: number;
  /** When false, the engine ignores the rule. */
  enabled: boolean;
  /** Optional per-rule mute window in seconds. */
  cooldown_sec?: number;
}

export interface AlertHit {
  id: string;
  rule_id: string;
  label: string;
  message: string;
  ts: number;
  acknowledged?: boolean;
}

const RULES_KEY = 'fasto-bi:alert-rules:v1';
const HISTORY_KEY = 'fasto-bi:alert-history:v1';
const HISTORY_MAX = 50;

const DEFAULT_RULES: AlertRule[] = [
  {
    id: 'order-stuck-30m',
    kind: 'order_age',
    label: 'Order stuck > 30 min',
    threshold: 30 * 60,
    enabled: false,
    cooldown_sec: 60,
  },
  {
    id: 'cancellation-spike',
    kind: 'cancellation_spike',
    label: 'Cancellation spike (>3/min)',
    threshold: 3,
    enabled: false,
    cooldown_sec: 120,
  },
];

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

function readRules(): AlertRule[] {
  try {
    const raw = localStorage.getItem(RULES_KEY);
    if (!raw) return DEFAULT_RULES;
    const parsed = JSON.parse(raw) as AlertRule[];
    if (!Array.isArray(parsed)) return DEFAULT_RULES;
    return parsed;
  } catch {
    return DEFAULT_RULES;
  }
}

function writeRules(rules: AlertRule[]) {
  try {
    localStorage.setItem(RULES_KEY, JSON.stringify(rules));
  } catch {
    /* storage full — ignore */
  }
}

function readHistory(): AlertHit[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as AlertHit[];
  } catch {
    return [];
  }
}

function writeHistory(h: AlertHit[]) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(h.slice(-HISTORY_MAX)));
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useAlertRules() {
  const [rules, setRules] = useState<AlertRule[]>(() => readRules());
  const [history, setHistory] = useState<AlertHit[]>(() => readHistory());

  const update = useCallback((id: string, patch: Partial<AlertRule>) => {
    setRules((prev) => {
      const next = prev.map((r) => (r.id === id ? { ...r, ...patch } : r));
      writeRules(next);
      return next;
    });
  }, []);

  const add = useCallback((rule: AlertRule) => {
    setRules((prev) => {
      const next = [...prev.filter((r) => r.id !== rule.id), rule];
      writeRules(next);
      return next;
    });
  }, []);

  const remove = useCallback((id: string) => {
    setRules((prev) => {
      const next = prev.filter((r) => r.id !== id);
      writeRules(next);
      return next;
    });
  }, []);

  const recordHit = useCallback((hit: AlertHit) => {
    setHistory((h) => {
      const next = [...h, hit].slice(-HISTORY_MAX);
      writeHistory(next);
      return next;
    });
  }, []);

  const ack = useCallback((id: string) => {
    setHistory((h) => {
      const next = h.map((x) => (x.id === id ? { ...x, acknowledged: true } : x));
      writeHistory(next);
      return next;
    });
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    writeHistory([]);
  }, []);

  return { rules, history, update, add, remove, recordHit, ack, clearHistory };
}

// ---------------------------------------------------------------------------
// Engine — mounted once at the shell level
// ---------------------------------------------------------------------------

interface OrderEvent {
  type?: string;
  order?: {
    id?: number;
    status?: string;
    created_timestamp?: string;
  };
}

/**
 * Mount this once near the top of the protected shell. It owns:
 *
 *   - A WS subscription to the global firehose (read-only).
 *   - A 30s ticker that re-evaluates "order_age" rules against an
 *     in-memory map of in-flight orders (the WS reducer keeps that map).
 *   - Cancellation rate tracking via a sliding window.
 *
 * The engine fires `Notification` (when permission is granted), plays a
 * soft chime, and toasts via Sonner. All hits are persisted so the
 * Stage 9 Alerts page can show history.
 */
export function useAlertEngine(enabled: boolean) {
  const { rules, recordHit } = useAlertRules();
  const lastHitRef = useRef<Map<string, number>>(new Map());
  const liveOrdersRef = useRef<Map<number, { ts: number; status: string }>>(new Map());
  const cancelTimesRef = useRef<number[]>([]);

  const onMessage = useCallback((ev: OrderEvent) => {
    if (!enabled) return;
    const o = ev?.order;
    if (!o?.id) return;

    if (ev.type === 'order_created') {
      liveOrdersRef.current.set(o.id, {
        ts: o.created_timestamp ? new Date(o.created_timestamp).getTime() : Date.now(),
        status: o.status ?? 'created',
      });
    } else if (ev.type === 'order_updated') {
      const existing = liveOrdersRef.current.get(o.id);
      if (existing) existing.status = o.status ?? existing.status;
      if (o.status === 'completed' || o.status === 'cancelled') {
        liveOrdersRef.current.delete(o.id);
      }
      if (o.status === 'cancelled') cancelTimesRef.current.push(Date.now());
    }
  }, [enabled]);

  useWebSocket<OrderEvent>({ path: enabled ? '/ws/orders/' : null, onMessage });

  useEffect(() => {
    if (!enabled) return;
    const t = window.setInterval(() => {
      const now = Date.now();
      const cooldown = (rule: AlertRule) => (rule.cooldown_sec ?? 30) * 1000;

      // --- order_age rules ---
      for (const rule of rules.filter((r) => r.enabled && r.kind === 'order_age')) {
        const last = lastHitRef.current.get(rule.id) ?? 0;
        if (now - last < cooldown(rule)) continue;
        for (const [orderId, info] of liveOrdersRef.current.entries()) {
          const ageSec = (now - info.ts) / 1000;
          if (ageSec >= rule.threshold) {
            fire(rule, `Order #${orderId} has been in ${info.status} for ${Math.round(ageSec / 60)}m.`, recordHit);
            lastHitRef.current.set(rule.id, now);
            break;
          }
        }
      }

      // --- cancellation_spike rules ---
      cancelTimesRef.current = cancelTimesRef.current.filter((ts) => now - ts < 60_000);
      for (const rule of rules.filter((r) => r.enabled && r.kind === 'cancellation_spike')) {
        const last = lastHitRef.current.get(rule.id) ?? 0;
        if (now - last < cooldown(rule)) continue;
        if (cancelTimesRef.current.length >= rule.threshold) {
          fire(rule, `${cancelTimesRef.current.length} cancellations in the last minute.`, recordHit);
          lastHitRef.current.set(rule.id, now);
        }
      }
    }, 30_000);
    return () => window.clearInterval(t);
  }, [enabled, rules, recordHit]);
}

function fire(rule: AlertRule, message: string, recordHit: (h: AlertHit) => void) {
  const hit: AlertHit = {
    id: `${rule.id}:${Date.now()}`,
    rule_id: rule.id,
    label: rule.label,
    message,
    ts: Date.now(),
  };
  recordHit(hit);
  notify.warning(rule.label, { description: message, duration: 6000 });

  // Mirror to the team inbox so other operators see the same hit. Best-effort.
  if (storage.getPrefs().share_alerts_with_team !== false) {
    void recordAlertEvent({
      message: `[${rule.label}] ${message}`,
      severity: 'warning',
      payload: { rule_kind: rule.kind, threshold: rule.threshold, source: 'local-engine' },
    }).catch(() => undefined);
  }

  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    try {
      new Notification(`Fasto BI · ${rule.label}`, { body: message, silent: false });
    } catch {
      /* notification rejected */
    }
  }
  playChime();
}

let _audio: HTMLAudioElement | null = null;
function playChime() {
  if (typeof Audio === 'undefined') return;
  // Defer construction so SSR / unit envs don't crash.
  if (!_audio) {
    try {
      _audio = new Audio('/sfx/new-order.mp3');
      _audio.volume = 0.4;
    } catch {
      _audio = null;
    }
  }
  _audio?.play?.().catch(() => {
    /* autoplay blocked — silent */
  });
}

/** Lazy convenience exposed for the Settings UI to ask the user for permission. */
export async function requestAlertPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  try {
    return await Notification.requestPermission();
  } catch {
    return 'denied';
  }
}

// Used by the Settings page if/when it wants to expose enable/disable globally.
export function isAlertEngineEnabled(): boolean {
  const prefs = storage.getPrefs();
  return prefs.alerts_enabled !== false;
}
